import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
} from '@mui/material';
import { IDK_SENTINEL } from './TestTaking';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import testService from '../../services/testService';
import { QuestionImage } from '../../components/common/QuestionImage';
import MathRenderer from '../../components/common/MathRenderer';

// Helper to format seconds to mm:ss
const formatTime = (seconds) => {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) return '--:--';
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const CONFIDENCE_OPTIONS = [
  { value: 'YES', label: 'Yes' },
  { value: 'SOMEWHAT', label: 'Somewhat' },
  { value: 'NO', label: 'No' }
];

const ReviewAnswers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { attemptId } = useParams();
const navState = location.state || {};

  const [questions, setQuestions] = useState(location.state?.questions || []);
  const [attempt, setAttempt] = useState(() => {
    if (navState.attemptMode) {
      return { mode: navState.attemptMode, start_time: navState.startTime, test_duration: navState.durationMinutes };
    }
    return null;
  });
  const [timeLeft, setTimeLeft] = useState(() => {
    if (navState.startTime && navState.durationMinutes && navState.attemptMode === 'EXAM') {
      const end = new Date(navState.startTime);
      end.setMinutes(end.getMinutes() + parseInt(navState.durationMinutes, 10));
      return Math.max(0, Math.floor((end - new Date()) / 1000));
    }
    return null;
  });
  const [answers, setAnswers] = useState(location.state?.answers || {});
  const [subAnswers, setSubAnswers] = useState(location.state?.subAnswers || {});
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  // Helper to derive displayable answer text (main answer or sub-answers)
  const getAnswerText = (question) => {
    if (answers[question.id] === IDK_SENTINEL) return IDK_SENTINEL;
    const subs = subAnswers[question.id];
    if (subs && Object.keys(subs).length > 0) {
      const parts = Object.entries(subs)
        .filter(([, v]) => v != null && String(v).trim() !== '')
        .map(([, v]) => String(v).trim());
      if (parts.length > 0) return parts.join('; ');
    }
    const rawAns = answers[question.id];
    if (rawAns === undefined || rawAns === null) return '';
    const opts = question.options ?? [];
    if (opts.length === 0) return rawAns;
    if (typeof opts[0] === 'object' && opts[0] !== null) {
      const match = opts.find(o => (o.id ?? o.option_id ?? o.value ?? o.key) == rawAns);
      if (match) return match.text || match.option_text || match.answer_text || match.label || rawAns;
    }
    const idx = typeof rawAns === 'number' ? rawAns - 1 : parseInt(rawAns, 10) - 1;
    if (!isNaN(idx) && idx >= 0 && idx < opts.length) return opts[idx];
    return rawAns;
  };
  // confidence state keyed by question id
  const [confidence, setConfidence] = useState({});
  const confidenceInitializedRef = useRef(false);
  
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const initialDataRef = useRef({ 
    hasQuestions: (location.state?.questions || []).length > 0,
    hasAnswers: Object.keys(location.state?.answers || {}).length > 0 || Object.keys(location.state?.subAnswers || {}).length > 0
  });

  // Fetch attempt and start countdown
  useEffect(() => {
    let intervalId;
    let isMounted = true;
    
    const init = async () => {
      try {
        setLoading(true);
        
        // Check if we have initial data from state
        const hasInitialData = initialDataRef.current.hasQuestions && initialDataRef.current.hasAnswers;
        
        if (!attemptId) {
          console.error('No attempt ID provided');
          setSnackbar({
            open: true,
            message: 'No attempt ID provided. Please try starting the test again.',
            severity: 'error',
            autoHideDuration: 5000
          });
          // Only redirect if we don't have questions/answers from state
          if (!hasInitialData) {
            setTimeout(() => {
              if (isMounted) navigate('/tests', { replace: true });
            }, 2000);
          } else {
            // We have data from state, allow user to continue
            setDataLoaded(true);
            setLoading(false);
          }
          return;
        }

        const data = await testService.getTestAttempt(attemptId);
        if (!isMounted) return;
        
        setAttempt(data);
        
        // Load questions and answers from attempt if not in state
        if (!questions.length && data.questions && Array.isArray(data.questions)) {
          setQuestions(data.questions);
        }
        
        if (!Object.keys(answers).length && data.answers && Array.isArray(data.answers)) {
          const savedAnswers = {};
          const savedSubAnswers = {};
          const questionMap = new Map();
          data.answers.forEach(a => {
            const qd = a.question_details;
            const qid = qd?.id || a.question;
            if (qid) {
              savedAnswers[qid] = a.answer;
              if (a.sub_answers && Array.isArray(a.sub_answers) && a.sub_answers.length > 0) {
                savedSubAnswers[qid] = {};
                a.sub_answers.forEach(sa => {
                  const sid = sa.sub_question_id ?? sa.sub_question;
                  if (sid != null) savedSubAnswers[qid][sid] = sa.answer ?? '';
                });
              }
              if (qd && !questionMap.has(qid)) {
                questionMap.set(qid, {
                  id: qid,
                  question_text: qd.text || qd.question_text,
                  question_type: qd.type || qd.question_type,
                  options: qd.options || [],
                  correct_answer: qd.correct_answer,
                  explanation: qd.explanation,
                  marks: qd.marks || 1,
                  image_url: qd.image_url || qd.image_proxy_url,
                  image_proxy_url: qd.image_proxy_url,
                  has_subquestions: qd.has_subquestions || (qd.sub_questions?.length > 0),
                  sub_questions: qd.sub_questions || [],
                  test_id: data.test_id || data.test
                });
              }
            }
          });
          if (Object.keys(savedAnswers).length > 0) setAnswers(savedAnswers);
          if (Object.keys(savedSubAnswers).length > 0) setSubAnswers(prev => ({ ...prev, ...savedSubAnswers }));
          if (questionMap.size > 0 && !data.questions?.length) {
            setQuestions(Array.from(questionMap.values()));
          }
        }
        
        // Check if attempt is already submitted - only redirect if it's THIS attempt
        if (data.status === 'SUBMITTED' || data.submitted || data.submitted_at) {
          console.log('Attempt already submitted, redirecting to results');
          const testId = data.test_id || location.state?.testId;
          if (testId) {
            setTimeout(() => {
              if (isMounted) navigate(`/test/${testId}/results/${attemptId}`, { replace: true });
            }, 500);
          } else {
            setTimeout(() => {
              if (isMounted) navigate('/tests', { replace: true });
            }, 500);
          }
          return;
        }
        
        // Additional check: verify this is the most recent attempt for the test
        // Only check if there's a submitted attempt with a DIFFERENT ID
        if (data.test_id) {
          try {
            const existingAttempts = await testService.getTestAttempts(data.test_id);
            if (!isMounted) return;
            
            const submittedAttempt = existingAttempts.find(attempt => 
              (attempt.status === 'SUBMITTED' || attempt.submitted || attempt.submitted_at) &&
              String(attempt.id) !== String(attemptId) // Only redirect if it's a different attempt
            );
            
            if (submittedAttempt) {
              console.log('Found different submitted attempt, redirecting to results');
              setTimeout(() => {
                if (isMounted) navigate(`/test/${data.test_id}/results/${submittedAttempt.id}`, { replace: true });
              }, 500);
              return;
            }
          } catch (error) {
            console.error('Error checking existing attempts:', error);
            // Don't redirect on error, just log it - allow user to continue
          }
        }
        
        setDataLoaded(true);
        
        // Set up timer if in EXAM mode
        if (data.mode === 'EXAM' && data.start_time && data.test_duration) {
          const endTime = new Date(data.start_time);
          endTime.setMinutes(endTime.getMinutes() + data.test_duration);
          
          const updateTimer = () => {
            if (!isMounted) return;
            const now = new Date();
            const diff = Math.max(0, endTime - now);
            const timeLeftSeconds = Math.ceil(diff / 1000);
            setTimeLeft(timeLeftSeconds);
            
            if (diff <= 0) {
              clearInterval(intervalId);
            }
          };
          
          updateTimer();
          intervalId = setInterval(updateTimer, 1000);
        }
        
      } catch (error) {
        console.error('Error fetching attempt:', error);
        if (!isMounted) return;
        
        // If we have questions/answers from state, don't redirect - allow user to continue
        const hasStateData = initialDataRef.current.hasQuestions && initialDataRef.current.hasAnswers;
        if (hasStateData) {
          console.log('Have questions/answers from state, allowing user to continue without warning');
          // Don't show warning if we have all necessary data from state
          // Create a minimal attempt object from state if needed
          if (!attempt && attemptId) {
            const testIdFromState = location.state?.testId || (questions.length > 0 ? questions[0]?.test_id : null);
            setAttempt({
              id: attemptId,
              mode: location.state?.attemptMode || 'EXAM',
              start_time: location.state?.startTime,
              test_duration: location.state?.durationMinutes,
              status: 'SUBMITTED', // Assume submitted for review scenario
              test_id: testIdFromState
            });
          }
          setDataLoaded(true); // Allow page to render
        } else {
          // Only redirect if we don't have any data
          setSnackbar({
            open: true,
            message: 'Failed to load test attempt. Please try starting the test again.',
            severity: 'error',
            autoHideDuration: 5000
          });
          setTimeout(() => {
            if (isMounted) navigate('/tests', { replace: true });
          }, 2000);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    init();
    
    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [attemptId, navigate, location.state?.testId]);

  // If navigation state missing AFTER data is loaded, redirect back only when we're sure data is missing
  useEffect(() => {
    if (!dataLoaded || loading) return;
    const hasAnswers = Object.keys(answers).length > 0 || Object.keys(subAnswers).length > 0;
    if (questions.length > 0 && hasAnswers) return;
    const timeoutId = setTimeout(() => {
      if (!questions.length || (!Object.keys(answers).length && !Object.keys(subAnswers).length)) {
        navigate(-1);
      }
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [questions, answers, subAnswers, dataLoaded, loading, navigate]);
  
  // Initialize confidence state when questions are available
  useEffect(() => {
    console.log('Initializing confidence state:', {
      questionsCount: questions?.length,
      confidenceInitialized: confidenceInitializedRef.current,
      hasLocationStateConfidence: !!location.state?.confidence
    });
    
    // Only initialize once when questions are available
    if (!confidenceInitializedRef.current && questions && questions.length > 0) {
      // Logic:
      // 1. If confidence comes from location.state (navigation within same session), use it
      // 2. Otherwise, initialize to undefined (first time or resumed session) - no default value
      if (location.state?.confidence && Object.keys(location.state.confidence).length > 0) {
        // Student navigated from test taking page with confidence selections
        // Retain those values, but convert empty strings to undefined
        const cleanedConfidence = {};
        Object.keys(location.state.confidence).forEach(qId => {
          const confValue = location.state.confidence[qId];
          // Convert empty strings to undefined so Select shows placeholder
          cleanedConfidence[qId] = (confValue === '' || confValue === null) ? undefined : confValue;
        });
        console.log('Using confidence from location state (current session):', cleanedConfidence);
        setConfidence(cleanedConfidence);
        confidenceInitializedRef.current = true;
      } else {
        // First time on review page OR resumed session
        // Always start with empty confidence (no default value)
        const initial = {};
        questions.forEach(q => {
          // Don't set any default - leave undefined so Select shows "Select..." placeholder
          initial[q.id] = undefined;
        });
        console.log('Initialized empty confidence state (first time or resumed session):', initial);
        setConfidence(initial);
        confidenceInitializedRef.current = true;
      }
    }
  }, [questions, location.state?.confidence]);
  
  // Debug: Log confidence state changes
  useEffect(() => {
    console.log('Confidence state changed:', confidence);
  }, [confidence]);
  
  const handleConfidenceChange = (questionId, value) => {
    console.log('Confidence change:', { questionId, value, currentConfidence: confidence });
    setConfidence(prev => {
      const newConfidence = { ...prev, [questionId]: value };
      console.log('Updated confidence state:', newConfidence);
      return newConfidence;
    });
    // Confidence is NOT saved to backend during selection
    // It will be saved only when submitting the test
  };

  const testId = questions[0]?.test_id || location.state?.testId || attempt?.test_id || attempt?.test?.id || '';

  // Check if all questions are answered or flagged as "I don't know"
  const allQuestionsAnswered = questions.every(q => {
    if (answers[q.id] === IDK_SENTINEL) return true;
    const subs = subAnswers[q.id];
    if (subs && Object.keys(subs).length > 0) {
      return Object.values(subs).every(v => v != null && String(v).trim() !== '');
    }
    const answer = answers[q.id];
    if (answer === undefined || answer === null) return false;
    if (typeof answer === 'string') return answer.trim() !== '';
    return true;
  });

  // Check if all confidence levels are selected (must not be undefined, null, or empty string)
  const allConfidenceSelected = questions.every(q => {
    const conf = confidence[q.id];
    return conf !== undefined && conf !== null && conf !== '';
  });

  const handleSubmit = async () => {
    // Check if all questions are answered or flagged as "I don't know"
    if (!allQuestionsAnswered) {
      setSnackbar({
        open: true,
        message: "Please answer every question or select \"I don't know\" before submitting.",
        severity: 'warning',
        autoHideDuration: 5000
      });
      return;
    }

    // Check if all confidence levels are selected
    if (!allConfidenceSelected || submitting) {
      if (!allConfidenceSelected) {
        setSnackbar({
          open: true,
          message: 'Please select confidence level for all questions before submitting.',
          severity: 'warning',
          autoHideDuration: 5000
        });
      }
      return;
    }
    
    // Check if time has expired - TEMPORARILY DISABLED
    // if (timeLeft !== null && timeLeft <= 0) {
    //   setSnackbar({ 
    //     open: true, 
    //     message: 'Time is up for this test attempt. Please contact your teacher.', 
    //     severity: 'error', 
    //     redirect: false 
    //   });
    //   return;
    // }
    
    setSubmitting(true);
    try {
      // prevent duplicate submission
      if (attemptId) {
        try {
          const latestAttempt = await testService.getTestAttempt(attemptId);
          if (latestAttempt.status === 'SUBMITTED' || latestAttempt.submitted || latestAttempt.submitted_at || latestAttempt.is_submitted) {
            console.log('Attempt already submitted, redirecting to results');
            setSnackbar({ open: true, message: 'Test is already submitted', severity: 'info', redirect: true });
            const testId = latestAttempt.test_id || location.state?.testId;
            if (testId) {
              navigate(`/test/${testId}/results/${attemptId}`, { replace: true });
            } else {
              navigate('/tests', { replace: true });
            }
            return;
          }
        } catch (error) {
          console.error('Error checking attempt status:', error);
          // Continue with submission if we can't check - might be network issue
        }
      }
      
      const formattedAnswers = questions.map(q => {
        const rawAnswer = answers[q.id];
        const isIdk = rawAnswer === IDK_SENTINEL;
        const normalizedAnswer = isIdk
          ? ''
          : (typeof rawAnswer === 'string' ? rawAnswer.trim() : rawAnswer);
        const conf = confidence[q.id];
        const validConfidence = ['YES', 'SOMEWHAT', 'NO'].includes(conf) ? conf : 'SOMEWHAT';
        return {
          question_id: q.id,
          answer: normalizedAnswer,
          is_idk: isIdk,
          confidence: validConfidence
        };
      });
      console.log('Submitting test with answers:', formattedAnswers);
      let meta = attempt && typeof attempt === 'object' ? attempt : null;
      if (attemptId && (!meta || meta.teacher_managed_student_id === undefined)) {
        try {
          meta = await testService.getTestAttempt(attemptId);
        } catch (_) {
          meta = meta || {};
        }
      }
      if (meta?.teacher_managed_student_id) {
        await testService.completeManagedAttempt(attemptId);
      } else {
        await testService.submitTestAttempt(attemptId, { answers: formattedAnswers });
      }
      navigate(`/test/${testId}/results/${attemptId}`, { replace: true });
    } catch (err) {
      console.error('Submission error:', err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          err.message || 
                          'Submission failed. Please try again.';
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error', 
        redirect: false 
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Derived counts for completion status card
  const answeredCount = questions.filter(q => {
    if (answers[q.id] === IDK_SENTINEL) return true;
    const subs = subAnswers[q.id];
    if (subs && Object.keys(subs).length > 0) return Object.values(subs).some(v => v != null && String(v).trim() !== '');
    const a = answers[q.id];
    return a !== undefined && a !== null && String(a).trim() !== '';
  }).length;
  const completionPct = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  const goToQuestion = (idx) =>
    navigate(`/test/${testId}`, { state: { questionIndex: idx, answers, subAnswers, attemptId, confidence, test: attempt?.test, questions, attemptMode: attempt?.mode, startTime: attempt?.start_time, durationMinutes: attempt?.test_duration, testId } });

  const confidencePillStyle = (qId, val) => {
    const selected = confidence[qId] === val;
    const base = { px: 1.5, py: 0.4, borderRadius: '20px', fontSize: '0.8125rem', fontFamily: 'inherit', fontWeight: 'inherit', cursor: 'pointer', userSelect: 'none' };
    if (selected) return { ...base, bgcolor: 'primary.main', color: '#fff', border: '1px solid', borderColor: 'primary.main' };
    return { ...base, bgcolor: '#e8eaf6', color: 'text.secondary', border: '1px solid #c5cae9', '&:hover': { bgcolor: '#d1d5f0' } };
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#eef0fb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box textAlign="center">
          <CircularProgress size={56} thickness={4} sx={{ color: '#283593' }} />
          <Typography variant="h6" sx={{ mt: 2, color: '#283593' }}>Loading review page…</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f0f2f9',
      backgroundImage: 'radial-gradient(circle, #c8cde8 1px, transparent 1px)',
      backgroundSize: '22px 22px',
      pb: 12,
    }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>

        {/* Top header row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          {/* Title + subtitle */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ color: 'text.primary', mb: 0.5 }}>
              Review Your Answers
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 520, lineHeight: 1.6 }}>
              Take a moment to check your responses. You can revisit any question or mark your confidence levels before final submission.
            </Typography>
          </Box>

          {/* Right: timer + completion card */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5 }}>
            {attempt?.mode === 'EXAM' && timeLeft !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'white', px: 1.5, py: 0.6, borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: '#283593' }} />
                <Typography variant="body2" sx={{ color: timeLeft < 60 ? 'error.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                  {formatTime(timeLeft)}
                </Typography>
                {timeLeft <= 0 && <Typography variant="caption" color="error.main">(Time expired)</Typography>}
              </Box>
            )}

            {/* Completion status card */}
            <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderRadius: 3, border: '1px solid #c5cae9', bgcolor: 'white', minWidth: 220 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                Completion Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5, mb: 0.8 }}>
                <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>{completionPct}% Done</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionPct}
                sx={{ height: 7, borderRadius: 4, bgcolor: '#e8eaf6', '& .MuiLinearProgress-bar': { bgcolor: '#283593', borderRadius: 4 } }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.6, display: 'block' }}>
                {answeredCount} of {questions.length} questions answered or flagged
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* Table card */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #c5cae9', overflow: 'hidden', boxShadow: '0 4px 24px rgba(108,99,255,0.07)' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', minWidth: 680 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  <TableCell sx={{ width: '5%', py: 1.2, color: 'text.secondary', borderBottom: '1px solid #c5cae9' }}>#</TableCell>
                  <TableCell sx={{ width: '34%', py: 1.2, color: 'text.secondary', borderBottom: '1px solid #c5cae9' }}>Question</TableCell>
                  <TableCell sx={{ width: '18%', py: 1.2, color: 'text.secondary', borderBottom: '1px solid #c5cae9' }}>Your Answer</TableCell>
                  <TableCell sx={{ width: '28%', py: 1.2, color: 'text.secondary', borderBottom: '1px solid #c5cae9', textAlign: 'center' }}>Are you confident?</TableCell>
                  <TableCell sx={{ width: '15%', py: 1.2, color: 'text.secondary', borderBottom: '1px solid #c5cae9' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {questions.map((q, idx) => {
                  const answerText = getAnswerText(q);
                  const isIdk = answerText === IDK_SENTINEL;
                  const isPending = !isIdk && (!answerText || answerText.trim() === '');
                  return (
                    <TableRow key={q.id} sx={{ '&:hover': { bgcolor: '#f5f6fe' }, '&:last-child td': { borderBottom: 0 } }}>
                      {/* # badge */}
                      <TableCell sx={{ py: 1.2, borderBottom: '1px solid #eef0f8' }}>
                        <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: '#283593', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 400 }}>
                          {idx + 1}
                        </Box>
                      </TableCell>

                      {/* Question */}
                      <TableCell sx={{ py: 1.2, borderBottom: '1px solid #eef0f8' }}>
                        <MathRenderer content={q.question_text || ''} variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', lineHeight: 1.5 }} />
                        {(q.image_proxy_url || q.image_url) && <QuestionImage question={q} alt="question" compact />}
                        {q.has_subquestions && Array.isArray(q.sub_questions) && q.sub_questions.length > 0 && (
                          <Box mt={0.5} sx={{ pl: 1.5 }}>
                            {q.sub_questions.map((subQ) => (
                              <Box key={subQ.id} sx={{ mb: 0.5 }}>
                                <Typography variant="caption" component="span">{subQ.letter ? `${subQ.letter}. ` : ''}</Typography>
                                <MathRenderer content={subQ.question_text || subQ.text || ''} variant="caption" />
                              </Box>
                            ))}
                          </Box>
                        )}
                      </TableCell>

                      {/* Your Answer */}
                      <TableCell sx={{ py: 1.2, borderBottom: '1px solid #eef0f8' }}>
                        {isIdk ? (
                          <Chip label="I don't know" size="small" color="warning" variant="outlined" sx={{ height: 22, borderRadius: '10px' }} />
                        ) : isPending ? (
                          <Chip label="Pending" size="small" color="error" variant="outlined" sx={{ height: 22, borderRadius: '10px' }} />
                        ) : (
                          <MathRenderer content={answerText} variant="body2" sx={{ color: 'primary.dark' }} />
                        )}
                      </TableCell>

                      {/* Confidence pills */}
                      <TableCell sx={{ py: 1.2, borderBottom: '1px solid #eef0f8', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', justifyContent: 'center' }}>
                          {CONFIDENCE_OPTIONS.map(opt => (
                            <Box
                              key={opt.value}
                              component="span"
                              onClick={() => handleConfidenceChange(q.id, opt.value)}
                              sx={confidencePillStyle(q.id, opt.value)}
                            >
                              {opt.label}
                            </Box>
                          ))}
                        </Box>
                      </TableCell>

                      {/* Action */}
                      <TableCell sx={{ py: 1.2, borderBottom: '1px solid #eef0f8' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {isPending ? (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => goToQuestion(idx)}
                              sx={{ borderRadius: 1, px: 1.5 }}
                            >
                              Solve Now
                            </Button>
                          ) : !isIdk ? (
                            <Button
                              size="small"
                              startIcon={<EditIcon sx={{ fontSize: '0.85rem !important' }} />}
                              onClick={() => goToQuestion(idx)}
                              sx={{ color: 'primary.main', px: 0.5 }}
                            >
                              Review
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              startIcon={<EditIcon sx={{ fontSize: '0.85rem !important' }} />}
                              onClick={() => goToQuestion(idx)}
                              sx={{ color: 'warning.main', px: 0.5 }}
                            >
                              Try Answer
                            </Button>
                          )}
                          {/* IDK toggle */}
                          <Button
                            size="small"
                            variant={isIdk ? 'contained' : 'outlined'}
                            color="warning"
                            onClick={() => {
                              const newVal = isIdk ? '' : IDK_SENTINEL;
                              setAnswers(prev => ({ ...prev, [q.id]: newVal }));
                            }}
                            sx={{ textTransform: 'none', borderRadius: 1, fontSize: '0.72rem', px: 1 }}
                          >
                            {isIdk ? '↩ Undo' : "I don't know"}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* Sticky bottom bar */}
      <Box sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10,
        bgcolor: 'white', borderTop: '1px solid #c5cae9', boxShadow: '0 -4px 16px rgba(108,99,255,0.08)',
        px: { xs: 2, md: 6 }, py: 1.5,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          onClick={() => goToQuestion(questions.length - 1)}
          sx={{ borderRadius: 1 }}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          disabled={!allQuestionsAnswered || !allConfidenceSelected || submitting}
          onClick={handleSubmit}
          endIcon={submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CheckIcon />}
          sx={{ borderRadius: 1, px: 3 }}
        >
          {submitting ? 'Submitting…' : 'Submit Test'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => {
          if (prev.redirect) navigate(`/test/${testId}/results/${attemptId}`, { replace: true });
          return { ...prev, open: false };
        })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewAnswers;
